import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CreditCard,
  Calendar,
  DollarSign,
  Copy,
  Check,
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { PerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { HttpCodes } from "../../lib/data";
import { FullPageLoader } from "../../components/ui/loader";
import { useUser } from "../../context/UserContext";
import { getOrgTokenKey } from "../../context/UserContext";
import { getCurrencySymbol } from "../../lib/data/currencies";
import { isCustomDomain } from "../../lib/utils/domain";

interface PaymentHistoryItem {
  id: number;
  pay_type: string;
  amount: string;
  reference: string;
  currency: string;
  data: string; // JSON string
  status: string;
  created_at: string;
  updated_at: string;
  attendee: number;
}

interface PaymentData {
  event_id: number;
  data: Array<{
    id: number;
    quantity: number;
  }>;
}

const PaymentHistory: React.FC = () => {
  const navigate = useNavigate();
  const { organizationSlug: urlOrganizationSlug } = useParams<{
    organizationSlug?: string;
  }>();
  const { userProfile } = useUser();
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedReference, setCopiedReference] = useState<string | null>(null);

  // Get organization slug - from URL if available, otherwise from user profile (for custom domains)
  const organizationSlug = useMemo(() => {
    if (urlOrganizationSlug) {
      return urlOrganizationSlug;
    }
    // If on custom domain, get slug from user profile
    if (isCustomDomain() && userProfile?.organizationSlug) {
      return userProfile.organizationSlug;
    }
    return "";
  }, [urlOrganizationSlug, userProfile]);

  useEffect(() => {
    if (organizationSlug) {
      fetchPaymentHistory();
    }
  }, [userProfile, organizationSlug]);

  const fetchPaymentHistory = async () => {
    if (!organizationSlug) {
      toast.error("Organization information is missing");
      navigate("/");
      return;
    }

    setIsLoading(true);
    try {
      const tokenKey = getOrgTokenKey(organizationSlug);
      const token = localStorage.getItem(tokenKey);

      if (!token) {
        toast.error("Please log in to view your payment history");
        navigate("/");
        return;
      }

      const response = await PerformRequest<{
        data: { data: PaymentHistoryItem[]; status: string; count: number };
      }>({
        method: Endpoints.GetPaymentHistory().method,
        route: Endpoints.GetPaymentHistory().route,
        doNotUseAuthorization: false,
        organizationSlug: organizationSlug,
      });

      console.log(response);

      if (response.status === HttpCodes.Success) {
        const paymentData =
          (response.data?.data?.data as PaymentHistoryItem[]) || [];
        setPayments(paymentData);
      } else {
        toast.error(response.message || "Failed to load payment history");
      }
    } catch (error: any) {
      console.error("Error fetching payment history:", error);
      toast.error(error.message || "Failed to load payment history");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyReference = (reference: string) => {
    navigator.clipboard.writeText(reference);
    setCopiedReference(reference);
    toast.success("Payment reference copied to clipboard!");
    setTimeout(() => setCopiedReference(null), 2000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parsePaymentData = (dataString: string): PaymentData | null => {
    try {
      return JSON.parse(dataString);
    } catch (e) {
      return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "success":
        return (
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Completed
          </span>
        );
      case "pending":
        return (
          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case "failed":
      case "cancelled":
        return (
          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
    }
  };

  if (isLoading) {
    return <FullPageLoader label="Loading payment history..." />;
  }

  if (payments.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#17265a] hover:text-[#0f1a3f] transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </motion.button>

          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CreditCard className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-[#17265a] mb-4">
              No Payment History
            </h2>
            <p className="text-gray-600 mb-8">
              You haven't made any payments yet. Purchase tickets to see your
              payment history here.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-[#17265a] text-white rounded-lg hover:bg-[#0f1a3f] transition-colors"
            >
              Browse Events
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#17265a] hover:text-[#0f1a3f] transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </motion.button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-[#17265a] mb-2">
                Payment History
              </h1>
              <p className="text-gray-600">
                {payments.length} payment{payments.length !== 1 ? "s" : ""}{" "}
                recorded
              </p>
            </div>
          </div>
        </motion.div>

        {/* Payments List */}
        <div className="space-y-4">
          {payments.map((payment, index) => {
            const paymentData = parsePaymentData(payment.data);
            const eventId = paymentData?.event_id;

            return (
              <motion.div
                key={payment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 shadow-sm backdrop-blur-sm hover:-translate-y-0.5 hover:shadow-md transition-all"
              >
                {/* Left accent bar */}
                <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#17265a] via-[#1f2f6b] to-[#2b3a7f]" />

                <div className="pl-4 pr-6 py-4 sm:pl-5 sm:py-5 md:py-6 md:pl-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-stretch md:justify-between md:gap-6">
                    {/* Left Section - Payment Details */}
                    <div className="flex-1">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#17265a] to-[#1f2f6b] shadow-sm sm:h-11 sm:w-11">
                          <CreditCard className="h-5 w-5 text-white" />
                        </div>

                        <div className="flex-1 space-y-2">
                          {/* Title + status + amount (mobile) */}
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <h3 className="text-sm font-semibold text-slate-900 sm:text-base">
                              {payment.pay_type === "ticket"
                                ? "Ticket Purchase"
                                : payment.pay_type.charAt(0).toUpperCase() +
                                  payment.pay_type.slice(1)}
                            </h3>
                            {getStatusBadge(payment.status)}

                            {/* Amount on small screens */}
                            <div className="ml-auto flex items-baseline gap-1 text-right text-[#17265a] md:hidden">
                              <span className="text-lg font-bold">
                                {getCurrencySymbol(payment.currency)}
                                {parseFloat(payment.amount).toFixed(2)}
                              </span>
                            </div>
                          </div>

                          {/* Meta row */}
                          <div className="grid gap-2 text-xs text-slate-600 sm:grid-cols-2 sm:text-sm">
                            <div className="flex items-center gap-1.5 sm:gap-2">
                              <Calendar className="h-4 w-4 flex-shrink-0" />
                              <span>{formatDate(payment.created_at)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 sm:justify-end sm:gap-2 text-xs sm:text-sm">
                              <DollarSign className="h-4 w-4 flex-shrink-0" />
                              <span className="text-slate-500">Amount:</span>
                              <span className="font-semibold text-slate-900">
                                {getCurrencySymbol(payment.currency)}
                                {parseFloat(payment.amount).toFixed(2)}{" "}
                                {payment.currency}
                              </span>
                            </div>
                          </div>

                          {/* Payment Reference */}
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                            <span className="text-slate-500">Reference:</span>
                            <div className="flex items-center gap-2">
                              <span className="max-w-[260px] truncate rounded bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-900 sm:max-w-xs sm:text-xs">
                                {payment.reference}
                              </span>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() =>
                                  handleCopyReference(payment.reference)
                                }
                                className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                              >
                                {copiedReference === payment.reference ? (
                                  <Check className="h-4 w-4 text-emerald-500" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </motion.button>
                            </div>
                          </div>

                          {/* Event link */}
                          {eventId && (
                            <div className="pt-2">
                              <button
                                onClick={() => {
                                  const orgSlug =
                                    userProfile?.organizationSlug ||
                                    organizationSlug;
                                  if (orgSlug) {
                                    navigate(`/event/${orgSlug}/${eventId}`);
                                  } else {
                                    navigate(`/event/${eventId}`);
                                  }
                                }}
                                className="inline-flex items-center gap-1 text-xs font-medium text-[#17265a] hover:text-[#0f1a3f]"
                              >
                                <span>View Event #{eventId}</span>
                                <ExternalLink className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right Section - Amount (desktop) */}
                    <div className="hidden min-w-[120px] shrink-0 items-center justify-end border-l border-slate-100 pl-4 text-right md:flex">
                      <div>
                        <div className="text-xl font-bold tracking-tight text-[#17265a]">
                          {getCurrencySymbol(payment.currency)}
                          {parseFloat(payment.amount).toFixed(2)}
                        </div>
                        <div className="mt-0.5 text-xs font-medium uppercase tracking-wide text-slate-400">
                          {payment.currency}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;
